import React from "react";
import { Button, Dropdown, Menu, Message, useMessagedAsyncFn } from "@payping/web-components";
import { DownOutlined } from "@payping/icons";
import { InvoiceComplexDetailViewModel, InvoiceStatusEnum, invoiceV2Apis, uploadV2Apis } from "@payping/api";
import { cancelInvoice, reminderInvoice } from "@payping/redux/actions";
import { ModalStaticFunctions } from "@payping/web-components/components/Modal/types";
import { generatePath, useNavigate } from "react-router-dom";
import { DashboardRoutes } from "@/router/constant";

import { useAppDispatch } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";

interface Props {
  data?: InvoiceComplexDetailViewModel;
  setManualPaymentNotesModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  loading?: boolean;
  locationSearch?: string;
  retry: () => void;
  retryList: () => void;
  modal: Omit<ModalStaticFunctions, "warn">;
  code: string;
}

const DetailOperations: React.FC<Props> = (props: Props) => {
  const { code, data, setManualPaymentNotesModalVisible, loading, locationSearch, modal, retry, retryList } = props;
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [, invoiceCancellationHandler] = useMessagedAsyncFn(
    async (schedule: boolean) => {
      if (code) await dispatch(cancelInvoice(code, schedule));
    },
    [code],
  );

  const handleCancel = React.useCallback(() => {
    modal.confirm({
      cancelText: t("cancel"),
      content: t("invoice.confirm.cancelRegularInvoice"),
      maskClosable: true,
      okText: t("ok"),
      onOk: async () => invoiceCancellationHandler(false),
    });
  }, [invoiceCancellationHandler, modal, t]);

  const handleCancelSchedule = React.useCallback(() => {
    modal.confirm({
      cancelText: t("cancel"),
      content: t("invoice.confirm.cancelScheduleInvoice"),
      maskClosable: true,
      okText: t("ok"),
      onOk: async () => invoiceCancellationHandler(true),
    });
  }, [invoiceCancellationHandler, modal, t]);

  const [, handleRemind] = useMessagedAsyncFn(async () => {
    await dispatch(reminderInvoice(code));
  }, []);
  const handleArchive = React.useCallback(async () => {
    if (data?.code) {
      if (!data?.archivedDateTime) {
        modal.confirm({
          cancelText: t("cancel"),
          content: t("invoice.archive.confirm"),
          maskClosable: true,
          okText: t("ok"),
          onOk: async () => {
            if (data.code) {
              await invoiceV2Apis.v2InvoiceArchiveByCodePOST(data.code);
              Message.success({ content: t("invoice.detail.archived") });
              retry();
              retryList();
            }
          },
        });
      } else {
        await invoiceV2Apis.v2InvoiceDeArchiveByCodePOST(data.code);
        Message.success({ content: t("invoice.detail.restored") });
        retry();
        retryList();
      }
    }
  }, [data?.archivedDateTime, data?.code, modal, retry, t]);

  const [, handleOfficialInvoice] = useMessagedAsyncFn(
    async (newWindow: Window | null) => {
      const response = await uploadV2Apis.generateOfficialInvoiceWithInvoiceCodeGet(code);
      newWindow?.location?.assign?.(response);
    },
    [code],
    undefined,
    { useProgress: true },
  );
  const [, handleReturnInvoice] = useMessagedAsyncFn(
    async () => {
      await invoiceV2Apis.v2InvoiceReturnPOST({ code });
      navigate(DashboardRoutes.myShop.invoice.root.url);
    },
    [code],
    undefined,
    { useProgress: true },
  );
  const [, handlePDF] = useMessagedAsyncFn(async (newWindow: Window | null) => {
    const key = Math.random().toString();
    Message.loading({ content: t("invoice.detail.makingPDF"), key });
    const resp = await uploadV2Apis.generateOfficialInvoiceWithInvoiceCodeGet(code);
    Message.success({ content: t("invoice.detail.done"), duration: 1, key });
    newWindow?.location?.assign?.(resp);
  }, []);

  const copyInvoice = () => {
    navigate(DashboardRoutes.myShop.invoice.create.url, { state: data });
  };

  const goToCorrection = () => {
    navigate(
      {
        pathname: DashboardRoutes.myShop.invoice.correction.url.replace(":code", code),
      },
      { state: { locationSearch } },
    );
  };

  const goToEdit = () => {
    navigate({ pathname: generatePath(DashboardRoutes.myShop.invoice.edit.url, { code }) }, { state: locationSearch });
  };

  const actions = (
    <Menu>
      {data?.status === InvoiceStatusEnum.PAID && (
        <>
          <Menu.Item
            key="0"
            onClick={() => {
              const newWindow = window.open(undefined, "_blank");
              handleOfficialInvoice(newWindow);
            }}
          >
            {t("invoice.officialInvoice.title")}
          </Menu.Item>
          <Menu.Item key="1" onClick={handleReturnInvoice}>
            {t("invoice.returnedInvoice")}
          </Menu.Item>
        </>
      )}
      {data?.status === InvoiceStatusEnum.DRAFT ? (
        <Menu.Item key="2" onClick={goToEdit}>
          {t("edit")}
        </Menu.Item>
      ) : null}
      {data?.status !== InvoiceStatusEnum.PAID && data?.status !== InvoiceStatusEnum.DRAFT && (
        <Menu.Item
          key="3"
          onClick={() => {
            setManualPaymentNotesModalVisible(true);
          }}
        >
          {t("invoice.detail.cashPayment")}
        </Menu.Item>
      )}
      {data?.status !== InvoiceStatusEnum.CANCELED &&
        data?.status !== InvoiceStatusEnum.PAID &&
        data?.status !== InvoiceStatusEnum.DRAFT && (
          <Menu.Item key="4" onClick={handleRemind}>
            {t("reminder")}
          </Menu.Item>
        )}

      <Menu.Item key="5" onClick={handleArchive}>
        {data?.archivedDateTime ? t("unArchive") : t("archive")}
      </Menu.Item>

      {data?.status === InvoiceStatusEnum.PAID ? (
        <Menu.Item key="6" onClick={goToCorrection}>
          {t("correction")}
        </Menu.Item>
      ) : null}

      {(data?.status === InvoiceStatusEnum.AWAITING_PAYMENT || data?.status === InvoiceStatusEnum.DELAYED) && (
        <Menu.Item key="7" onClick={handleCancel}>
          {t("cancel")}
        </Menu.Item>
      )}

      <Menu.Item key="8" onClick={copyInvoice}>
        {t("copy")}
      </Menu.Item>
      {data?.paymentCode ? (
        <Menu.Item
          key="9"
          onClick={() => {
            navigate({
              pathname: DashboardRoutes.mySale.report.url.replace(":code", data.paymentCode || ""),
            });
          }}
        >
          {t("invoice.transactionDetails")}
        </Menu.Item>
      ) : null}
      <Menu.Item
        key="10"
        onClick={() => {
          const newWindow = window.open(undefined, "_blank");
          handlePDF(newWindow);
        }}
      >
        {t("pdf")}
      </Menu.Item>
      {data?.isSchedule && (
        <Menu.Item key="11" onClick={handleCancelSchedule}>
          {t("invoice.detail.cancelScheduling")}
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <Dropdown overlay={actions}>
      <Button loading={loading} type="primary" ghost icon={<DownOutlined width={14} height={14} />} iconPosition="end">
        {t("operation")}
      </Button>
    </Dropdown>
  );
};

export default DetailOperations;
